/* eslint-disable quote-props */
import React from 'react';
import { today, getLocalTimeZone } from '@internationalized/date';
import FormProvider from '../../primitives/form-provider';
import Input from '../../primitives/input';
import Select, { SelectOption } from '../../primitives/select';
import TextArea from '../../primitives/text-area';
import FormRegion from '../../primitives/form-region';
import FormFieldset from '../../primitives/form-fieldset';
import StyledButton from '../../primitives/styled-button';
import Typography from '../../primitives/typography';
import Divider from '../../primitives/divider';
import { useLocator } from '../../../contexts/franchise/locator-provider';
import { colors } from '../../primitives/colors';
import RadioGroup, { RadioItem } from '../../primitives/radio-group';
import DatePicker from '../../primitives/date-picker';
import Spinner from '../../primitives/spinner';
import useQueryParams from '../../../hooks/use-query-params';
import { formatPhoneNumber } from '../../utils/format-phone-link';
import { usStatesAndTerritories, canadianProvincesAndTerritories } from '../../../constants/states';

type LossTypeFields = {
    [key: string]: {
        name: string,
        causes: (keyof typeof LOSS_CAUSE_FIELDS)[]
    }
};

const usAndCanStates = usStatesAndTerritories.concat(canadianProvincesAndTerritories);

// no 'deleted' loss types
const LOSS_TYPE_FIELDS: LossTypeFields = {
    '1': { name: 'Water', causes: ['1', '8', '11', '12', '14', '18', '21', '23', '28', '29', '31', '10', '34', '16', '17', '15', '32'] },
    '2': { name: 'Fire', causes: ['4', '5', '7', '9', '13', '19', '20', '27', '33', '48', '16', '15', '32'] },
    '3': { name: 'Mold', causes: ['1', '8', '11', '12', '14', '18', '21', '23', '28', '29', '31', '10', '34', '17', '15', '32'] },
    '6': { name: 'Fire/Water', causes: ['4', '5', '7', '9', '13', '19', '20', '27', '33', '48', '16', '15', '32'] },
    '7': { name: 'Liability', causes: ['1', '20', '27', '33', '10', '16', '15'] },
    '8': { name: 'Odor', causes: ['2', '7', '9', '14', '22', '26', '15', '32'] },
    '9': { name: 'Other', causes: ['15'] },
    '10': { name: 'Sewage', causes: ['1', '8', '11', '12', '14', '18', '21', '23', '28', '29', '31', '57', '10', '34', '17', '15', '32'] },
    '11': { name: 'Smoke', causes: ['4', '5', '7', '9', '13', '19', '20', '27', '33', '48', '16', '15', '32'] },
    '12': { name: 'Tree Removal', causes: ['15'] },
    '13': { name: 'Vandalism', causes: ['15', '32'] },
    '14': { name: 'Biohazard', causes: ['49', '50', '52', '53', '15'] },
    '17': { name: 'General Cleaning', causes: ['47', '15'] },
    '18': { name: 'Contents', causes: ['55', '40', '54', '56', '45', '41', '44', '15'] },
    '19': { name: 'Reconstruction', causes: ['55', '40', '54', '45', '41', '44', '15'] },
};

const LOSS_CAUSE_FIELDS: Record<string, string> = {
    '1': 'AC Leak',
    '2': 'Animal',
    '3': 'Burglary',
    '4': 'Candle',
    '5': 'Dishwasher',
    '7': 'Electrical Malfunction',
    '8': 'Flood',
    '9': 'Grease',
    '10': 'Hot Water Heater',
    '11': 'Hurricane',
    '12': 'Ice Storm',
    '13': 'Lightning',
    '14': 'Mildew',
    '15': 'Other',
    '16': 'Other Appliance',
    '17': 'Other Weather',
    '18': 'Pipe',
    '19': 'Puffback-Furnace',
    '20': 'Refrigerator',
    '21': 'Roof Leak',
    '22': 'Sewage Back-Up',
    '23': 'Slab Leak',
    '24': 'Soot',
    '26': 'Stain on Carpet',
    '27': 'Stove/Oven',
    '28': 'Sump Pump Failure',
    '29': 'Toilet',
    '30': 'Tree',
    '31': 'Tornado',
    '32': 'Unknown',
    '33': 'Washing Machine',
    '34': 'Wind',
    '40': 'Fire',
    '41': 'Smoke',
    '44': 'Water',
    '45': 'Sewage',
    '47': 'Pathogen / CSC',
    '48': 'Wildfires',
    '49': 'Death',
    '50': 'Injury',
    '52': 'Sharps',
    '53': 'Waste',
    '54': 'Mold',
    '55': 'Biohazard',
    '56': 'Reconstruction',
    '57': 'Tree Roots',
};

type GeocodeRawHint = {
    text: string
    magicKey?: string
    place_id?: string
} & unknown;

type FormFields = {
    FranchiseNumber: string,
    FirstName: string,
    LastName: string,
    Address1: string,
    Address2: string,
    City: string,
    State: string,
    Zip: string,
    PrimaryPhone: string,
    Email: string,
    LossDate: string,
    PropertyType: string,
    LossCause: string,
    LossType: string,
    Comments: string,
    InteractionId: string
};

type GeocodeEarthHint = {
    label: string
    data: GeocodeRawHint
};

interface FnolFormProps {
    submitButtonText?: string
    successMessageText?: string
}

const placeholderStyleOverride = {
    '&:not(:placeholder-shown) ~ div label': {
        backgroundColor: colors.shadesWhite,
    },
};

const hidePlaceholder = {
    '&:not(:placeholder-shown) ~ div label': {
        backgroundColor: 'transparent',
        color: 'transparent',
    },
};

const FnolForm = ({ submitButtonText, successMessageText }: FnolFormProps): JSX.Element => {
    const {
        ANI: customerPhoneFromParam,
        FID: franchiseFromParam, // TODO: does the endpoint take in string or number???????
        CID: InteractionIDFromParam,
    } = useQueryParams();
    const [lossType, setLossType] = React.useState<keyof typeof LOSS_TYPE_FIELDS | null>(null);
    const suggestionsRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [suggestionQuery, setSuggestionQuery] = React.useState('');
    const [overrideFields, setOverrideFields] = React.useState<{
        address1: string
        address2: string,
        city: string
        state: string
        zip: string
        phone: string
    }>({
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        phone: customerPhoneFromParam as string || '',
    });
    const [suggestions, setSuggestion] = React.useState<GeocodeEarthHint[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [formSubmitSuccess, setFormSubmitSuccess] = React.useState(false);
    const [formSubmitFail, setFormSubmitFail] = React.useState(false);
    const [loadingSuggestion, setLoadingSuggestion] = React.useState(false);

    const scrollToTop = (): void => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    };

    const { geo } = useLocator();

    const getSuggestions = React.useCallback(
        (value: string) => {
            const type = 'address';
            const url = `/api/arcgis-suggest?${new URLSearchParams({
                text: value,
                types: type,
                radius: '200000',
                latitude: `${geo?.latitude || ''}`,
                longitude: `${geo?.longitude || ''}`,
                category: 'Address',
            }).toString()}`;

            setLoadingSuggestion(true);

            fetch(url)
                .then(async (response) => {
                    if (!response.ok) {
                        throw new Error('Something went wrong with the call to geocode suggest');
                    }
                    const result: GeocodeRawHint[] = await response.json();
                    if (!Array.isArray(result)) {
                        return null;
                    }
                    setSuggestion(result.map(item => ({
                        data: item,
                        label: item.text,
                    })));
                    setLoadingSuggestion(false);
                    return null;
                })
                .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    setSuggestion([]);
                    setLoadingSuggestion(false);
                });
        },
        [geo?.latitude, geo?.longitude],
    );

    React.useEffect(() => {
        if (suggestionQuery) {
            // this length is arbitrary.open to suggestions
            if (suggestionQuery.length > 3) {
                getSuggestions(suggestionQuery);
            } else {
                setSuggestion([]);
                setLoadingSuggestion(false);
            }
        } else {
            // this is for when there's an empty string as a value
            setSuggestion([]);
        }
    }, [getSuggestions, suggestionQuery]);

    const selectSuggestion = (hint: GeocodeRawHint): void => {
        setLoadingSuggestion(true);
        const url = `/api/geocode?${(new URLSearchParams({
            placeId: hint.place_id || '',
            magicKey: hint.magicKey || '',
            address: hint.text || '',
        })).toString()}`;
        fetch(url)
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Something went wrong with the call to geocode');
                }
                const data = await response.json();
                if (data) {
                    setSuggestion([]);
                    setOverrideFields(e => ({
                        ...e,
                        address1: data.address1,
                        address2: data.address2,
                        city: data.city,
                        state: data.stateShort,
                        zip: data.zip,
                    }));
                }
                setLoadingSuggestion(false);
                return null;
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                setSuggestion([]);
                setLoadingSuggestion(false);
            });
    };

    const onSubmit = (e: Record<string, string>): void => {
        setLoading(true);
        const isUrgent = e['immediate-help'] === 'Yes';
        const urgentMessage = isUrgent ? '** CUSTOMER NEEDS IMMEDIATE ASSISTANCE ** -- ' : '';

        const formObject: FormFields = {
            FranchiseNumber: franchiseFromParam as string,
            FirstName: e['given-name'],
            LastName: e['family-name'],
            Address1: e.address1,
            Address2: e.address2,
            City: e.city,
            State: e.state,
            Zip: e.zip,
            PrimaryPhone: e.phone,
            // SecondaryPhone: '',
            Email: e.email,
            LossDate: e['loss-date'],
            PropertyType: e['property-type'],
            LossCause: e['loss-cause'],
            LossType: e['loss-type'],
            Comments: `${urgentMessage}${e.comments || ''}`,
            InteractionId: InteractionIDFromParam as string,
        };
        // submit

        try {
            const payload = {
                Priority: 1,
                ...formObject,
            };
            payload.PrimaryPhone = formatPhoneNumber(payload.PrimaryPhone);
            // payload.SecondaryPhone = formatPhoneNumber(payload.SecondaryPhone)

            fetch('/api/forms/form-FnolForm', {
                method: 'POST',
                body: JSON.stringify({ data: payload }),
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                },
            })
                .then((res) => {
                    if (!res.ok) {
                        throw new Error('Something went wrong with the form-FnolForm api');
                    }
                    return res.json();
                }).then((data) => {
                    setLoading(false);
                    if ('success' in data) {
                        scrollToTop();
                        if (data.success) {
                            setFormSubmitSuccess(true);
                        } else {
                            setFormSubmitFail(true);
                        }
                    }
                    return null;
                })
                .catch((err) => {
                    scrollToTop();
                    // eslint-disable-next-line no-console
                    console.log('err', err);
                    setLoading(false);
                    setFormSubmitFail(true);
                });
        } catch (err) {
            scrollToTop();
            // eslint-disable-next-line no-console
            console.log('err', err);
            setLoading(false);
            setFormSubmitFail(true);
        }
    };
    return (!formSubmitSuccess && !formSubmitFail) ? (
        <FormProvider disabled={loading} onSubmit={onSubmit} values={overrideFields}>
            <FormRegion description="Contact Information">
                <FormFieldset name="Name">
                    <Input autoFocus name="given-name" placeholder="First Name" autoComplete="given-name" required css={placeholderStyleOverride} />
                    <Input name="family-name" placeholder="Last Name" autoComplete="family-name" required css={placeholderStyleOverride} />
                </FormFieldset>
                <FormFieldset name="Address">
                    <div css={{
                        width: 'calc(75% - 4px)',
                        position: 'relative',
                    }}
                    >
                        <div css={{
                            position: 'relative',
                            zIndex: 10,
                            borderRadius: '4px',
                            width: '100%',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0), 0 4px 6px -2px rgba(0, 0, 0, 0)',
                            transition: '0.3s box-shadow',
                            input: {
                                backgroundColor: suggestionQuery ? colors.grey100 : colors.grey200,
                            },
                            '&:focus-within': {
                                '.suggestions': {
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                    display: (suggestions && suggestions.length > 0) ? 'block' : 'none',
                                    borderBottomLeftRadius: '4px',
                                    borderBottomRightRadius: '4px',
                                },
                                input: {
                                    borderBottomLeftRadius: (suggestions && suggestions.length > 0) ? '0px' : '4px',
                                    borderBottomRightRadius: (suggestions && suggestions.length > 0) ? '0px' : '4px',
                                },
                            },
                        }}
                        >
                            <div
                                css={{
                                    position: 'absolute',
                                    right: '30px',
                                    zIndex: 1,
                                    top: '15px',
                                }}
                                style={{
                                    opacity: loadingSuggestion ? 1 : 0,
                                }}
                            >
                                <Spinner
                                    size="sm"
                                />
                            </div>
                            <Input
                                required
                                ref={inputRef}
                                name="address1"
                                type="text"
                                css={placeholderStyleOverride}
                                placeholder="Street Address"
                                onChange={(e) => {
                                    if (e?.currentTarget?.value) {
                                        setSuggestionQuery(e.currentTarget.value);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowDown' && suggestions && suggestions.length > 0) {
                                        e.preventDefault();
                                        suggestionsRef.current?.querySelector('button')?.focus();
                                    } else if (e.key === 'ArrowUp' && suggestions && suggestions.length > 0) {
                                        e.preventDefault();
                                        if (suggestionsRef.current) {
                                            const selector = suggestionsRef.current?.querySelector('button:last-child');
                                            if (selector && 'focus' in selector && typeof selector.focus === 'function') {
                                                selector.focus();
                                            }
                                        }
                                    }
                                }}
                            />
                            {suggestions && suggestions.length > 0 && (
                                <div
                                    className="suggestions"
                                    css={{
                                        display: 'none',
                                        maxWidth: '100%',
                                        marginTop: '2px',
                                        position: 'absolute',
                                        backgroundColor: colors.shadesWhite,
                                        '@media (max-width: 767px)': {
                                            width: 'calc(100vw - 65px)',
                                            maxWidth: 'calc(100vw - 65px)',
                                            left: 0,
                                        },
                                    }}
                                    ref={suggestionsRef}
                                    tabIndex={-1}
                                    role="listbox"
                                >
                                    {suggestions.map(item => (
                                        <button
                                            key={item.label}
                                            type="button"
                                            role="option"
                                            aria-label={item.label}
                                            aria-selected="false"
                                            css={{
                                                display: 'flex',
                                                paddingLeft: '16px',
                                                paddingRight: '16px',
                                                alignItems: 'center',
                                                width: '100%',
                                                height: '40px',
                                                borderStyle: 'none',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                transition: '0.2s',
                                                '&:hover, &:focus': {
                                                    backgroundColor: colors.grey200,
                                                    paddingLeft: '20px',
                                                },
                                                '&:last-of-type': {
                                                    borderBottomLeftRadius: '4px',
                                                    borderBottomRightRadius: '4px',
                                                },
                                            }}
                                            onClick={() => {
                                                selectSuggestion(item.data);
                                            }}
                                            onKeyDown={(e) => {
                                                // should also handle the escape kye
                                                if (e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                    const currentSuggestionElement = e.currentTarget;
                                                    const nextSuggestionElement = currentSuggestionElement
                                                        .nextElementSibling as HTMLButtonElement | undefined;
                                                    if (nextSuggestionElement) {
                                                        nextSuggestionElement.focus();
                                                    } else if (inputRef.current) {
                                                        inputRef.current.focus();
                                                    }
                                                }

                                                if (e.key === 'ArrowUp') {
                                                    e.preventDefault();
                                                    const currentSuggestionElement = e.currentTarget;
                                                    const prevSuggestionElement = currentSuggestionElement
                                                        .previousElementSibling as HTMLButtonElement | undefined;

                                                    if (prevSuggestionElement) {
                                                        prevSuggestionElement.focus();
                                                    } else if (inputRef.current) {
                                                        inputRef.current.focus();
                                                    }
                                                }
                                            }}
                                        >
                                            <span css={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                            >
                                                {item.label}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div css={{
                        width: 'calc(25% - 4px)',
                    }}
                    >
                        <Input
                            name="address2"
                            placeholder="Apt./Suite"
                            autoComplete="address-line2"
                            css={placeholderStyleOverride}
                        />
                    </div>
                    <Input
                        name="city"
                        placeholder="City"
                        autoComplete="address-level2"
                        required
                        css={placeholderStyleOverride}
                    />
                    <Select name="state" placeholder='State' required css={hidePlaceholder}>
                        {usAndCanStates.map(state => (
                            <SelectOption key={state.abbreviation} value={state.abbreviation}>
                                {state.name}
                            </SelectOption>
                        ))}
                    </Select>
                    <Input
                        name="zip"
                        placeholder="Zip"
                        autoComplete="postal-code"
                        required
                        css={placeholderStyleOverride}
                    />
                </FormFieldset>
                <FormFieldset name="Phone">
                    <Input name="phone" type="tel" validationRegex={/^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/g} placeholder="Phone Number" autoComplete="tel" required css={hidePlaceholder} />
                </FormFieldset>
                <FormFieldset name="Email">
                    <Input name="email" placeholder="Email" autoComplete="email" css={hidePlaceholder} />
                </FormFieldset>
            </FormRegion>
            <FormRegion description="Loss Information">
                <FormFieldset name="Loss Date">
                    {/* Loss Date / Time here */}
                    <div>
                        <DatePicker name="loss-date" placeholder="Loss Date" required maxValue={today(getLocalTimeZone())} />
                    </div>
                </FormFieldset>
                <FormFieldset name="Property Type">
                    <Select name="property-type" placeholder='Property Type' required css={hidePlaceholder}>
                        <SelectOption value="1">
                            Residential
                        </SelectOption>
                        <SelectOption value="2">
                            Commercial
                        </SelectOption>
                    </Select>
                </FormFieldset>
                <FormFieldset name="Loss Info">
                    <Select
                        name="loss-type"
                        placeholder="Loss Type"
                        css={placeholderStyleOverride}
                        onChange={(e) => {
                            setLossType(e as keyof typeof LOSS_TYPE_FIELDS);
                        }}
                        required
                    >
                        {Object.entries(LOSS_TYPE_FIELDS).map(([key, value]) => (
                            <SelectOption value={key}>
                                {value.name}
                            </SelectOption>
                        ))}
                    </Select>
                    {lossType && (
                        <Select name="loss-cause" key={lossType} placeholder="Loss Cause" required css={placeholderStyleOverride}>
                            {LOSS_TYPE_FIELDS[lossType] && LOSS_TYPE_FIELDS[lossType].causes.map(causeCode => (
                                <SelectOption value={causeCode}>{LOSS_CAUSE_FIELDS[causeCode]}</SelectOption>
                            ))}
                        </Select>
                    )}
                </FormFieldset>
                <FormFieldset name="Do you need service immediately?">
                    <RadioGroup name="immediate-help" required>
                        <RadioItem value="Yes">
                            Yes
                        </RadioItem>
                        <RadioItem value="No">
                            No
                        </RadioItem>
                    </RadioGroup>
                </FormFieldset>
                <FormFieldset name="Comments">
                    <TextArea name="comments" placeholder="" css={placeholderStyleOverride} />
                </FormFieldset>
                {/* <FormFieldset name="Uploads">
                    FIELD MISSING HERE
                    <Typography css={{ width: '100%' }} fontSize={{ dt: 'sm' }}>
                        You may upload up to 2 photos to help our team determine your needs faster.
                    </Typography>
                </FormFieldset> */}
            </FormRegion>
            <FormRegion description="">
                <FormFieldset name="">
                    <StyledButton type="submit" loading={loading}>
                        {submitButtonText || 'Submit'}
                    </StyledButton>
                </FormFieldset>
                <FormFieldset name="">
                    <Typography css={{ width: '100%' }} fontSize={{ dt: 'sm' }}>
                        <Divider css={{
                            paddingBottom: '4px',
                        }}
                        />
                        Submitting this form will forward your contact information to your local SERVPRO,
                        who will contact you with next steps.
                    </Typography>
                </FormFieldset>
            </FormRegion>
        </FormProvider>
    ) : (
        <>
            {formSubmitSuccess && (
                <div
                    style={{ paddingTop: '16px' }}
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{ __html: successMessageText || '' }}
                />
            )}
            {formSubmitFail && <div css={{ color: colors.statesError, paddingTop: '16px' }}>Something went wrong with your submission.</div>}
        </>
    );
};

export default FnolForm;
